// src/pages/dashboard/ContactFormManager.tsx
import React, { useState, useEffect } from "react";
import { useDarkMode } from "../../context/DarkModeContext";
import {
  Search,
  Mail,
  Trash2,
  Reply,
  ChevronDown,
  ChevronUp,
  // Filter,
  Clock,
  CheckCircle,
  AlertCircle,
  MessageCircle,
  // Eye,
  // MoreVertical,
} from "lucide-react";

// Import Skeleton components
import {
  // Skeleton,
  // SkeletonText,
  SkeletonTable,
  // SkeletonCard,
  // SkeletonList,
} from "../../components/Skeleton";
import Skeleton from "../../components/Skeleton";

interface ContactForm {
  id: string;
  name: string;
  email: string;
  subject: string;
  category: string;
  message: string;
  status: "pending" | "in_progress" | "responded" | "closed" | "spam";
  priority: "low" | "medium" | "high" | "urgent";
  adminNotes?: string;
  response?: string;
  respondedBy?: string;
  respondedAt?: string;
  createdAt: string;
  updatedAt: string;
  respondedByUser?: {
    id: string;
    name: string;
    email: string;
  };
}

// Get API base URL from environment variable
const API_BASE = import.meta.env.VITE_API_BASE || "http://localhost:5000/api";

const ContactFormsManager: React.FC = () => {
  const { isDark } = useDarkMode();
  const [contactForms, setContactForms] = useState<ContactForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedForm, setSelectedForm] = useState<ContactForm | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseText, setResponseText] = useState("");
  const [sendEmailNotifications, setSendEmailNotifications] = useState(true);
  const [filters, setFilters] = useState({
    status: "all",
    category: "all",
    priority: "all",
    search: "",
  });
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    responded: 0,
    closed: 0,
  });

  // Fetch contact forms
  const fetchContactForms = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();

      if (filters.status !== "all")
        queryParams.append("status", filters.status);
      if (filters.category !== "all")
        queryParams.append("category", filters.category);
      if (filters.priority !== "all")
        queryParams.append("priority", filters.priority);
      if (filters.search) queryParams.append("search", filters.search);
      queryParams.append("sortBy", sortConfig.key);
      queryParams.append("sortOrder", sortConfig.direction);

      const response = await fetch(
        `${API_BASE}/admin/contacts?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );
      const data = await response.json();

      if (data.contactForms) {
        setContactForms(data.contactForms);
        // Calculate stats
        const statsData = {
          total: data.contactForms.length,
          pending: data.contactForms.filter(
            (form: ContactForm) => form.status === "pending",
          ).length,
          responded: data.contactForms.filter(
            (form: ContactForm) => form.status === "responded",
          ).length,
          closed: data.contactForms.filter(
            (form: ContactForm) => form.status === "closed",
          ).length,
        };
        setStats(statsData);
      }
    } catch (error) {
      console.error("Error fetching contact forms:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContactForms();
  }, [filters, sortConfig]);

  const handleStatusUpdate = async (
    contactId: string,
    status: ContactForm["status"],
  ) => {
    try {
      const response = await fetch(
        `${API_BASE}/admin/contacts/${contactId}/status`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status }),
        },
      );

      if (response.ok) {
        fetchContactForms();
        if (selectedForm?.id === contactId) {
          setSelectedForm({ ...selectedForm, status });
        }
      }
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleSubmitResponse = async (contactId: string) => {
    if (!responseText.trim()) return;

    try {
      // Choose endpoint based on email notification preference
      const endpoint = sendEmailNotifications
        ? `${API_BASE}/admin/contacts/${contactId}/send-response`
        : `${API_BASE}/admin/contacts/${contactId}/response`;

      const method = sendEmailNotifications ? "POST" : "PATCH";

      const response = await fetch(endpoint, {
        method,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ response: responseText }),
      });

      if (response.ok) {
        setShowResponseModal(false);
        setResponseText("");
        fetchContactForms();

        // Show success message
        alert(
          sendEmailNotifications
            ? "Response sent successfully via email!"
            : "Response saved successfully!",
        );
      }
    } catch (error) {
      console.error("Error submitting response:", error);
      alert("Error submitting response. Please try again.");
    }
  };

  const handleDelete = async (contactId: string) => {
    if (!confirm("Are you sure you want to delete this contact form?")) return;

    try {
      const response = await fetch(`${API_BASE}/admin/contacts/${contactId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.ok) {
        fetchContactForms();
        if (selectedForm?.id === contactId) {
          setShowModal(false);
          setSelectedForm(null);
        }
      }
    } catch (error) {
      console.error("Error deleting contact form:", error);
    }
  };

  const getStatusColor = (status: ContactForm["status"]) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700";
      case "in_progress":
        return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700";
      case "responded":
        return "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700";
      case "closed":
        return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700";
      case "spam":
        return "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700";
    }
  };

  const getPriorityColor = (priority: ContactForm["priority"]) => {
    switch (priority) {
      case "urgent":
        return "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700";
      case "high":
        return "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700";
      case "medium":
        return "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700";
      case "low":
        return "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700";
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "technical":
        return "🔧";
      case "billing":
        return "💰";
      case "account":
        return "👤";
      case "feature":
        return "💡";
      case "bug":
        return "🐛";
      default:
        return "📧";
    }
  };

  const handleSort = (key: string) => {
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  // Skeleton components for different sections
  const renderStatsSkeleton = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {[1, 2, 3, 4].map((index) => (
        <div
          key={index}
          className={`p-4 rounded-lg border ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
          }`}
        >
          <div className="flex justify-between items-start">
            <div className="space-y-2 flex-1">
              <Skeleton
                variant="text"
                width="40%"
                animation="pulse"
                theme={isDark ? "dark" : "light"}
              />
              <Skeleton
                variant="text"
                width="60%"
                animation="pulse"
                className="h-6"
                theme={isDark ? "dark" : "light"}
              />
            </div>
            <Skeleton
              variant="circle"
              width={40}
              height={40}
              animation="pulse"
              theme={isDark ? "dark" : "light"}
            />
          </div>
        </div>
      ))}
    </div>
  );

  const renderFiltersSkeleton = () => (
    <div
      className={`mb-6 p-4 rounded-lg ${
        isDark
          ? "bg-gray-800 border border-gray-700"
          : "bg-white border border-gray-200"
      }`}
    >
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {[1, 2, 3, 4, 5].map((index) => (
          <Skeleton
            key={index}
            variant="input"
            animation="pulse"
            theme={isDark ? "dark" : "light"}
            className="h-10"
          />
        ))}
      </div>
    </div>
  );

  const renderTableSkeleton = () => (
    <div
      className={`rounded-lg shadow-lg ${
        isDark
          ? "bg-gray-800 border border-gray-700"
          : "bg-white border border-gray-200"
      }`}
    >
      <SkeletonTable
        rows={5}
        columns={7}
        withHeader={true}
        theme={isDark ? "dark" : "light"}
        className="p-4"
      />
    </div>
  );

  return (
    <div
      className={`min-h-screen py-6 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${
        isDark ? "bg-gray-900 text-gray-100" : "bg-gray-50 text-gray-900"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          {loading ? (
            <div className="space-y-2">
              <Skeleton
                variant="text"
                width="60%"
                animation="pulse"
                theme={isDark ? "dark" : "light"}
                className="h-8"
              />
              <Skeleton
                variant="text"
                width="40%"
                animation="pulse"
                theme={isDark ? "dark" : "light"}
                className="h-4"
              />
            </div>
          ) : (
            <>
              <h1
                className={`text-3xl font-bold mb-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Contact Forms Management
              </h1>
              <p
                className={`text-lg ${
                  isDark ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Manage and respond to user inquiries and support requests
              </p>
            </>
          )}
        </div>

        {/* Stats Overview */}
        {loading ? (
          renderStatsSkeleton()
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Total Contacts */}
            <div
              className={`p-4 rounded-lg border ${
                isDark
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              <div className="flex justify-between items-start">
                <div>
                  <p
                    className={`text-sm ${
                      isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Total Contacts
                  </p>
                  <p
                    className={`text-2xl font-bold ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {stats.total}
                  </p>
                </div>
                <div
                  className={`p-2 rounded-lg ${
                    isDark ? "bg-blue-900/30" : "bg-blue-50"
                  }`}
                >
                  <MessageCircle className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </div>

            {/* Pending */}
            <div
              className={`p-4 rounded-lg border ${
                isDark
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              <div className="flex justify-between items-start">
                <div>
                  <p
                    className={`text-sm ${
                      isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Pending
                  </p>
                  <p
                    className={`text-2xl font-bold ${
                      isDark ? "text-yellow-300" : "text-yellow-600"
                    }`}
                  >
                    {stats.pending}
                  </p>
                </div>
                <div
                  className={`p-2 rounded-lg ${
                    isDark ? "bg-yellow-900/30" : "bg-yellow-50"
                  }`}
                >
                  <Clock className="w-6 h-6 text-yellow-500" />
                </div>
              </div>
            </div>

            {/* Responded */}
            <div
              className={`p-4 rounded-lg border ${
                isDark
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              <div className="flex justify-between items-start">
                <div>
                  <p
                    className={`text-sm ${
                      isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Responded
                  </p>
                  <p
                    className={`text-2xl font-bold ${
                      isDark ? "text-green-300" : "text-green-600"
                    }`}
                  >
                    {stats.responded}
                  </p>
                </div>
                <div
                  className={`p-2 rounded-lg ${
                    isDark ? "bg-green-900/30" : "bg-green-50"
                  }`}
                >
                  <CheckCircle className="w-6 h-6 text-green-500" />
                </div>
              </div>
            </div>

            {/* Closed */}
            <div
              className={`p-4 rounded-lg border ${
                isDark
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              <div className="flex justify-between items-start">
                <div>
                  <p
                    className={`text-sm ${
                      isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Closed
                  </p>
                  <p
                    className={`text-2xl font-bold ${
                      isDark ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {stats.closed}
                  </p>
                </div>
                <div
                  className={`p-2 rounded-lg ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <AlertCircle className="w-6 h-6 text-gray-500" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters and Search */}
        {loading ? (
          renderFiltersSkeleton()
        ) : (
          <div
            className={`mb-6 p-4 rounded-lg ${
              isDark
                ? "bg-gray-800 border border-gray-700"
                : "bg-white border border-gray-200"
            }`}
          >
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="md:col-span-2">
                <div className="relative">
                  <Search
                    className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${
                      isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                  />
                  <input
                    type="text"
                    placeholder="Search contacts..."
                    value={filters.search}
                    onChange={(e) =>
                      setFilters({ ...filters, search: e.target.value })
                    }
                    className={`w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>
              </div>
              <select
                value={filters.status}
                onChange={(e) =>
                  setFilters({ ...filters, status: e.target.value })
                }
                className={`px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="responded">Responded</option>
                <option value="closed">Closed</option>
                <option value="spam">Spam</option>
              </select>
              <select
                value={filters.category}
                onChange={(e) =>
                  setFilters({ ...filters, category: e.target.value })
                }
                className={`px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                <option value="all">All Categories</option>
                <option value="general">General</option>
                <option value="technical">Technical</option>
                <option value="billing">Billing</option>
                <option value="account">Account</option>
                <option value="feature">Feature Request</option>
                <option value="bug">Bug Report</option>
                <option value="other">Other</option>
              </select>
              <select
                value={filters.priority}
                onChange={(e) =>
                  setFilters({ ...filters, priority: e.target.value })
                }
                className={`px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                <option value="all">All Priorities</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>
        )}

        {/* Contact Forms List */}
        {loading ? (
          renderTableSkeleton()
        ) : contactForms.length === 0 ? (
          <div
            className={`p-8 text-center rounded-lg ${
              isDark
                ? "bg-gray-800 border border-gray-700"
                : "bg-white border border-gray-200"
            }`}
          >
            <Mail
              className={`w-16 h-16 mx-auto mb-4 ${
                isDark ? "text-gray-600" : "text-gray-400"
              }`}
            />
            <p
              className={`text-lg ${
                isDark ? "text-gray-400" : "text-gray-500"
              }`}
            >
              No contact forms found
            </p>
            {filters.search ||
            filters.status !== "all" ||
            filters.category !== "all" ||
            filters.priority !== "all" ? (
              <button
                onClick={() =>
                  setFilters({
                    status: "all",
                    category: "all",
                    priority: "all",
                    search: "",
                  })
                }
                className={`mt-4 px-4 py-2 rounded-lg font-medium ${
                  isDark
                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
              >
                Clear Filters
              </button>
            ) : null}
          </div>
        ) : (
          <div
            className={`rounded-lg shadow-lg ${
              isDark
                ? "bg-gray-800 border border-gray-700"
                : "bg-white border border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr
                    className={`border-b ${
                      isDark ? "border-gray-700" : "border-gray-200"
                    }`}
                  >
                    <th
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort("name")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Contact</span>
                        {sortConfig.key === "name" &&
                          (sortConfig.direction === "asc" ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          ))}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort("subject")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Subject</span>
                        {sortConfig.key === "subject" &&
                          (sortConfig.direction === "asc" ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          ))}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                      Priority
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort("status")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Status</span>
                        {sortConfig.key === "status" &&
                          (sortConfig.direction === "asc" ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          ))}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort("createdAt")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Date</span>
                        {sortConfig.key === "createdAt" &&
                          (sortConfig.direction === "asc" ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          ))}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {contactForms.map((form) => (
                    <tr
                      key={form.id}
                      className={`hover:${
                        isDark ? "bg-gray-700" : "bg-gray-50"
                      } cursor-pointer`}
                      onClick={() => {
                        setSelectedForm(form);
                        setShowModal(true);
                      }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div
                            className={`font-medium ${
                              isDark ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {form.name}
                          </div>
                          <div
                            className={`text-sm ${
                              isDark ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            {form.email}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div
                          className={`text-sm ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {form.subject}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <span>{getCategoryIcon(form.category)}</span>
                          <span
                            className={`text-sm capitalize ${
                              isDark ? "text-gray-300" : "text-gray-600"
                            }`}
                          >
                            {form.category.replace("_", " ")}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(
                            form.priority,
                          )}`}
                        >
                          {form.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(
                            form.status,
                          )}`}
                        >
                          {form.status.replace("_", " ")}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div
                          className={`text-sm ${
                            isDark ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {new Date(form.createdAt).toLocaleDateString()}
                        </div>
                        <div
                          className={`text-xs ${
                            isDark ? "text-gray-500" : "text-gray-400"
                          }`}
                        >
                          {new Date(form.createdAt).toLocaleTimeString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedForm(form);
                              setShowResponseModal(true);
                              setResponseText(form.response || "");
                            }}
                            className={`p-1 rounded ${
                              isDark
                                ? "text-blue-400 hover:text-blue-300 hover:bg-blue-900/30"
                                : "text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                            }`}
                            title="Respond"
                          >
                            <Reply className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(form.id);
                            }}
                            className={`p-1 rounded ${
                              isDark
                                ? "text-red-400 hover:text-red-300 hover:bg-red-900/30"
                                : "text-red-600 hover:text-red-800 hover:bg-red-50"
                            }`}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* View Contact Form Modal */}
        {showModal && selectedForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div
              className={`max-w-2xl w-full rounded-lg shadow-xl max-h-[90vh] overflow-y-auto ${
                isDark ? "bg-gray-800" : "bg-white"
              }`}
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <h2
                    className={`text-xl font-semibold ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Contact Form Details
                  </h2>
                  <button
                    onClick={() => setShowModal(false)}
                    className={`p-1 rounded ${
                      isDark
                        ? "text-gray-400 hover:text-gray-300 hover:bg-gray-700"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <span className="sr-only">Close</span>
                    <span className="text-2xl">×</span>
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Contact Information */}
                  <div>
                    <h3
                      className={`text-lg font-medium mb-4 ${
                        isDark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Contact Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label
                          className={`text-sm font-medium ${
                            isDark ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          Name
                        </label>
                        <p
                          className={`mt-1 ${
                            isDark ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {selectedForm.name}
                        </p>
                      </div>
                      <div>
                        <label
                          className={`text-sm font-medium ${
                            isDark ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          Email
                        </label>
                        <p
                          className={`mt-1 ${
                            isDark ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {selectedForm.email}
                        </p>
                      </div>
                      <div>
                        <label
                          className={`text-sm font-medium ${
                            isDark ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          Category
                        </label>
                        <p
                          className={`mt-1 capitalize ${
                            isDark ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {selectedForm.category.replace("_", " ")}
                        </p>
                      </div>
                      <div>
                        <label
                          className={`text-sm font-medium ${
                            isDark ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          Priority
                        </label>
                        <p
                          className={`mt-1 capitalize ${
                            isDark ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {selectedForm.priority}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <h3
                      className={`text-lg font-medium mb-4 ${
                        isDark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Message
                    </h3>
                    <div
                      className={`p-4 rounded-lg ${
                        isDark ? "bg-gray-700" : "bg-gray-50"
                      }`}
                    >
                      <h4
                        className={`font-semibold mb-2 ${
                          isDark ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {selectedForm.subject}
                      </h4>
                      <p
                        className={`whitespace-pre-wrap ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        {selectedForm.message}
                      </p>
                    </div>
                  </div>

                  {/* Admin Response */}
                  {selectedForm.response && (
                    <div>
                      <h3
                        className={`text-lg font-medium mb-4 ${
                          isDark ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Admin Response
                      </h3>
                      <div
                        className={`p-4 rounded-lg ${
                          isDark ? "bg-blue-900/20" : "bg-blue-50"
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <span
                            className={`text-sm font-medium ${
                              isDark ? "text-blue-300" : "text-blue-700"
                            }`}
                          >
                            Response by:{" "}
                            {selectedForm.respondedByUser?.name || "Admin"}
                          </span>
                          <span
                            className={`text-xs ${
                              isDark ? "text-blue-400" : "text-blue-600"
                            }`}
                          >
                            {selectedForm.respondedAt
                              ? new Date(
                                  selectedForm.respondedAt,
                                ).toLocaleString()
                              : "N/A"}
                          </span>
                        </div>
                        <p
                          className={`whitespace-pre-wrap ${
                            isDark ? "text-blue-200" : "text-blue-800"
                          }`}
                        >
                          {selectedForm.response}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Status Management */}
                  <div>
                    <h3
                      className={`text-lg font-medium mb-4 ${
                        isDark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Status Management
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {[
                        "pending",
                        "in_progress",
                        "responded",
                        "closed",
                        "spam",
                      ].map((status) => (
                        <button
                          key={status}
                          onClick={() =>
                            handleStatusUpdate(
                              selectedForm.id,
                              status as ContactForm["status"],
                            )
                          }
                          className={`px-3 py-1 rounded text-sm font-medium border transition-colors ${
                            selectedForm.status === status
                              ? getStatusColor(selectedForm.status)
                              : isDark
                                ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                                : "bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200"
                          }`}
                        >
                          {status.replace("_", " ")}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => {
                        setShowResponseModal(true);
                        setResponseText(selectedForm.response || "");
                      }}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        isDark
                          ? "bg-blue-600 hover:bg-blue-700 text-white"
                          : "bg-blue-600 hover:bg-blue-700 text-white"
                      }`}
                    >
                      {selectedForm.response
                        ? "Update Response"
                        : "Add Response"}
                    </button>
                    <button
                      onClick={() => handleDelete(selectedForm.id)}
                      className={`px-4 py-2 rounded-lg font-medium border transition-colors ${
                        isDark
                          ? "border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                          : "border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                      }`}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Response Modal */}
        {showResponseModal && selectedForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div
              className={`max-w-md w-full rounded-lg shadow-xl ${
                isDark ? "bg-gray-800" : "bg-white"
              }`}
            >
              <div className="p-6">
                <h2
                  className={`text-xl font-semibold mb-4 ${
                    isDark ? "text-white" : "text-gray-900"
                  }`}
                >
                  {selectedForm.response ? "Update Response" : "Add Response"}
                </h2>

                <textarea
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  rows={8}
                  placeholder="Type your response here..."
                  className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }`}
                />

                {/* Email notification toggle */}
                <div className="mt-4 flex items-center">
                  <input
                    type="checkbox"
                    id="sendEmail"
                    checked={sendEmailNotifications}
                    onChange={(e) =>
                      setSendEmailNotifications(e.target.checked)
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                  />
                  <label
                    htmlFor="sendEmail"
                    className={`ml-2 text-sm ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Send email notification to user
                  </label>
                </div>

                <div className="flex justify-end space-x-4 mt-6">
                  <button
                    onClick={() => {
                      setShowResponseModal(false);
                      setResponseText("");
                    }}
                    className={`px-4 py-2 rounded-lg font-medium border transition-colors ${
                      isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleSubmitResponse(selectedForm.id)}
                    disabled={!responseText.trim()}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      !responseText.trim()
                        ? "bg-gray-400 cursor-not-allowed text-white dark:bg-gray-600"
                        : isDark
                          ? "bg-blue-600 hover:bg-blue-700 text-white"
                          : "bg-blue-600 hover:bg-blue-700 text-white"
                    }`}
                  >
                    {sendEmailNotifications ? "Send Response" : "Save Response"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactFormsManager;
